import * as React from 'react'
import {
  fetchPendingAgentWrites,
  onDomainTopic,
  pendingAgentWriteToEntry,
  type InFlightAgentActionProposal
} from '@axilon-apps/domains-ontology-ui-state'

import { AgentActionApprovalPanel, type AgentActionSettlement } from '../AgentActionApproval'

interface IngestConfirmedWritesPanelProps {
  /** Job id — also the `conversation_id` for ingest agent_writes rows. */
  jobId: string
  /** Job branch — Apply must target this writable branch, not main. */
  branchId?: string
  /** When false, stop refreshing (terminal job). */
  active?: boolean
}

type ConfirmedGraphProposal = Extract<
  InFlightAgentActionProposal,
  { kind: 'cypher_write' | 'graph_write' }
>

function isConfirmedGraphProposal(p: InFlightAgentActionProposal): p is ConfirmedGraphProposal {
  return p.kind === 'cypher_write' || p.kind === 'graph_write'
}

const CONFIRMED_GRAPH_ACTION_KINDS = new Set(['cypher_write', 'graph_write'])

/**
 * Lists pending ingest `propose_cypher_writes` rows, refreshed on agent-write
 * wake-ups, and hosts the shared approval panel. Park resume after
 * Apply/Reject is owned by the API routes — do not fire the query
 * orchestrator plan-resume thunk.
 */
export function IngestConfirmedWritesPanel({
  jobId,
  branchId,
  active = true
}: IngestConfirmedWritesPanelProps) {
  const [proposals, setProposals] = React.useState<Record<string, InFlightAgentActionProposal>>({})

  const refresh = React.useCallback(async () => {
    try {
      const writes = await fetchPendingAgentWrites(jobId)
      setProposals((prev) => {
        const next: Record<string, InFlightAgentActionProposal> = { ...prev }
        const pendingIds = new Set<string>()
        for (const write of writes) {
          // Missing actionKind = legacy cypher_write rows predating the column.
          if (write.actionKind && !CONFIRMED_GRAPH_ACTION_KINDS.has(write.actionKind)) continue
          const { id, proposal } = pendingAgentWriteToEntry(write)
          if (!isConfirmedGraphProposal(proposal)) continue
          pendingIds.add(id)
          // Keep local terminalState / settledMeta after Apply/Reject —
          // the pending endpoint no longer returns those rows.
          if (next[id]?.terminalState !== undefined) continue
          next[id] = proposal
        }
        for (const id of Object.keys(next)) {
          if (!pendingIds.has(id) && next[id]?.terminalState === undefined) {
            delete next[id]
          }
        }
        return next
      })
    } catch {
      // Keep last-known cards; the next refresh may recover.
    }
  }, [jobId])

  React.useEffect(() => {
    if (!jobId || !active) return
    void refresh()
    return onDomainTopic('agent-writes', () => void refresh())
  }, [jobId, active, refresh])

  const onSettled = React.useCallback((settlement: AgentActionSettlement) => {
    setProposals((prev) => {
      const existing = prev[settlement.toolUseId]
      if (!existing) return prev
      return {
        ...prev,
        [settlement.toolUseId]: {
          ...existing,
          ...(settlement.settledMeta ?? {}),
          terminalState: settlement.terminal
        }
      }
    })
  }, [])

  const onRemoveSettled = React.useCallback((toolUseIds: string[]) => {
    setProposals((prev) => {
      const next = { ...prev }
      for (const id of toolUseIds) delete next[id]
      return next
    })
  }, [])

  if (Object.keys(proposals).length === 0) return null

  return (
    <AgentActionApprovalPanel
      proposals={proposals}
      conversationId={jobId}
      branchId={branchId}
      firePlanResume={false}
      onSettled={onSettled}
      onRemoveSettled={onRemoveSettled}
      applyBlockedReason={
        branchId ? null : 'Job branch is still loading — Apply will unlock once it is available.'
      }
      className="border-t-0 border-b border-border"
    />
  )
}

export default IngestConfirmedWritesPanel
